import { createAsyncThunk } from '@reduxjs/toolkit'
import { baseUrl, headersDefault } from '.'
import axios from 'axios'
import { axiosGetRead } from '../../services/readRequest'
import { toErrorText } from '../../services/errorText'

/**
 * Re-throw a request failure carrying the SERVER's message, not axios's.
 *
 * createAsyncThunk serialises `error.message`, and axios's own is "Request
 * failed with status code 403" — which is what the member would be shown. The
 * Community block's refusal is written by the backend on purpose, so its
 * wording can be changed without shipping an app release; it has to survive the
 * trip through the thunk to be worth anything.
 */
const rethrowWithServerMessage = (error) => {
	const body = error?.response?.data
	const text = body ? toErrorText(body, error?.message) : error?.message
	const wrapped = new Error(text || 'Si è verificato un errore.')
	wrapped.status = error?.response?.status
	throw wrapped
}

export const getGroupPosts = createAsyncThunk(
	'groups/getGroupPosts',
	async ({
		page = 1,
		per_page,
		is_fixed = false,
		is_liked = false,
		is_favorite = false,
		starting_id,
		after_id,
		dir,
		order_by,
		from_main_group = true,
		group_id,
		search,
	}) => {
		const effectivePerPage =
			per_page ??
			(order_by === 'suggested' ? 3 : page === 1 ? 3 : 5)
		const params = {
			page,
			per_page: effectivePerPage,
			...(is_favorite
				? { is_favorite: true }
				: is_liked
					? { is_liked: true }
					: { is_fixed }),
			...(starting_id ? { starting_id } : {}),
			// Keyset cursor for Nuovi post: continue strictly after this post
			// instead of by offset, which drifts as posts are published.
			...(after_id ? { after_id } : {}),
			...(dir ? { dir } : {}),
			...(order_by ? { order_by } : {}),
			...(from_main_group ? { from_main_group } : { group_id }),
			...(search ? { search } : {}),
		}

		const response = await axiosGetRead(
			`${baseUrl}/api/master-club-post-groups/list`,
			{
				headers: headersDefault(),
				params,
			}
		)

		return response.data
	}
)

export const likePost = createAsyncThunk(
	'groups/likePost',
	async ({ element_id, is_fixed = false, from_main_group = true, reaction_type }) => {
		const body = {
			element_type: 'App\\Models\\MasterClubPostGroup',
			element_id,
		}
		// Reactions (love/hug/haha/sad/wow) chosen by long-pressing the post's
		// "Mi piace"; omitted → plain like.
		if (reaction_type) body.reaction_type = reaction_type

		try {
			const response = await axios.post(
				`${baseUrl}/api/master-club-likes`,
				body,
				{
					headers: headersDefault(),
				}
			)

			return response.data
		} catch (error) {
			rethrowWithServerMessage(error)
		}
	}
)

// Pick/swap/remove a reaction (love/hug/haha/sad/wow or plain like) on a POST.
// Unlike likePost (a blind toggle), the caller passes the exact transition
// (delta + willBeLiked + reaction_type) it computed from the user's current
// reaction, so the store stays correct when SWAPPING one reaction for another
// (the like count doesn't change on a swap).
export const reactToPost = createAsyncThunk(
	'groups/reactToPost',
	async ({ element_id, reaction_type }) => {
		const body = {
			element_type: 'App\\Models\\MasterClubPostGroup',
			element_id,
		}
		if (reaction_type) body.reaction_type = reaction_type

		try {
			const response = await axios.post(
				`${baseUrl}/api/master-club-likes`,
				body,
				{
					headers: headersDefault(),
				}
			)

			return response.data
		} catch (error) {
			rethrowWithServerMessage(error)
		}
	}
)

export const toggleFavoritePost = createAsyncThunk(
	'groups/toggleFavoritePost',
	async ({ element_id }) => {
		const response = await axios.post(
			`${baseUrl}/api/v1/master-club-post-groups/${element_id}/favorite`,
			{},
			{
				headers: headersDefault(),
			}
		)

		return response.data
	}
)

export const recordPostShare = createAsyncThunk(
	'groups/recordPostShare',
	async ({ element_id, event_id }) => {
		const response = await axios.post(
			`${baseUrl}/api/v1/master-club-post-groups/${element_id}/shares`,
			{ event_id },
			{
				headers: headersDefault(),
			}
		)

		return response.data
	}
)

export const publishPostGroup = createAsyncThunk(
	'groups/publishPostGroup',
	async ({ post_group_id, status_id, denied_reason }) => {
		const response = await fetch(
			`${baseUrl}/api/master-club-post-groups/${post_group_id}`,
			{
				body: JSON.stringify({ status_id, denied_reason }),
				method: 'PATCH',
				headers: headersDefault(),
			}
		)

		return response
	}
)

export const getGroup = createAsyncThunk('groups/getGroup', async (groupId) => {
	const response = await axiosGetRead(
		`${baseUrl}/api/master-club-groups/${groupId}/fetch`,
		{
			headers: headersDefault(),
		}
	)

	return response.data
})

export const getGroupsList = createAsyncThunk(
	'groups/getGroupsList',
	async () => {
		const params = { type: 'post' }

		const response = await axiosGetRead(`${baseUrl}/api/master-club-groups/list`, {
			headers: headersDefault(),
			params,
		})

		return response.data
	}
)

export const getPost = createAsyncThunk(
	'groups/getPost',
	async ({ postId, page, aroundCommentId, perPage }) => {
		const params = new URLSearchParams({ page })
		// Deep link from a notification: ask the API to stretch this first page
		// until it reaches that comment, so the sheet has something to scroll
		// to even after newer comments pushed it off page one. The API answers
		// with the page size it actually used, which later pages echo back so
		// the list keeps lining up.
		if (aroundCommentId) params.set('around_comment_id', aroundCommentId)
		if (perPage) params.set('per_page', perPage)

		const response = await axiosGetRead(
			`${baseUrl}/api/master-club-post-groups/${postId}/fetch?${params.toString()}`,
			{
				headers: headersDefault(),
			}
		)

		return response.data
	}
)

export const addPost = createAsyncThunk('groups/addPost', async (body) => {
	const response = await axios.post(`${baseUrl}/api/master-club-posts`, body, {
		headers: headersDefault(),
	})

	return response.data
})

export const likeComment = createAsyncThunk(
	'groups/likeComment',
	async ({ element_id, reaction_type }) => {
		const body = { element_type: 'App\\Models\\MasterClubComment', element_id }
		// Comment reactions (love/hug/haha/sad/wow). Omitted = the plain
		// "Mi piace"; the backend treats a repeat of the same reaction as a
		// toggle-off and a different one as a swap.
		if (reaction_type) body.reaction_type = reaction_type

		try {
			const response = await axios.post(
				`${baseUrl}/api/master-club-likes`,
				body,
				{
					headers: headersDefault(),
				}
			)

			return response.data
		} catch (error) {
			rethrowWithServerMessage(error)
		}
	}
)

const commentElement = async ({ element_type, element_id, content, image, video, mentions, reply_to_name }) => {
	const body = { element_type, element_id, content, image, video, mentions, reply_to_name }
	try {
		const response = await axios.post(
			`${baseUrl}/api/master-club-comments`,
			body,
			{
				headers: headersDefault(),
			}
		)

		return response.data
	} catch (error) {
		rethrowWithServerMessage(error)
	}
}

export const commentPost = createAsyncThunk(
	'groups/commentPost',
	async (params) =>
		await commentElement({
			...params,
			element_type: 'App\\Models\\MasterClubPostGroup',
		})
)

export const replyComment = createAsyncThunk(
	'groups/replyComment',
	async (params) =>
		await commentElement({
			...params,
			element_type: 'App\\Models\\MasterClubComment',
		})
)

export const getCommentAndReplies = createAsyncThunk(
	'groups/getCommentAndReplies',
	async ({ commentId, page = 1 }) => {
		const url = `${baseUrl}/api/master-club-comments/${commentId}/fetch`

		const params = { page }
		const headers = headersDefault()
		const config = { headers, params }

		const response = await axiosGetRead(url, config)

		return response.data
	}
)
